import React, { useEffect, useRef, useState, useCallback } from "react";
import { Send } from "lucide-react";
import api from "../api";

export default function ChatThread({ threadType, threadId, meId, placeholder = "Write a message…", height = "h-72" }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    if (!threadId) return;
    try {
      const { data } = await api.get("/messages", { params: { thread_type: threadType, thread_id: threadId } });
      setMsgs(data);
    } catch (e) { /* ignore */ }
  }, [threadType, threadId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await api.post("/messages", { thread_type: threadType, thread_id: threadId, body: body.trim() });
      setBody("");
      await load();
    } catch (e) { /* ignore */ } finally { setSending(false); }
  };

  return (
    <div className="rounded-xl border border-black/10 bg-white overflow-hidden">
      <div className={`${height} overflow-y-auto p-3 space-y-2 bg-[#fdfaf7]`}>
        {msgs.length === 0 ? (
          <p className="text-[13px] text-[#8a8285] text-center mt-6">No messages yet. Say hello!</p>
        ) : msgs.map((m) => {
          const mine = m.sender_id === meId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[14px] ${mine ? "bg-[#7c2233] text-white rounded-br-sm" : "bg-white border border-black/10 text-[#1f1a1b] rounded-bl-sm"}`}>
                {!mine && <p className="text-[11px] font-medium text-[#7c2233] mb-0.5">{m.sender_name}</p>}
                <p className="whitespace-pre-wrap leading-snug">{m.body}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-black/10 p-2.5">
        <input value={body} onChange={(e) => setBody(e.target.value)} placeholder={placeholder} data-testid="chat-input"
          className="flex-1 h-10 rounded-lg border border-black/10 px-3 text-[14px] outline-none focus:border-[#7c2233]" />
        <button type="submit" disabled={sending} aria-label="Send" data-testid="chat-send" className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-lg bg-[#7c2233] hover:bg-[#661b29] disabled:opacity-60 text-white transition-colors">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
